<script setup>
import { computed } from 'vue';
import { useMapGetter } from 'dashboard/composables/store';
import Button from 'dashboard/components-next/button/Button.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';

const props = defineProps({
  modelValue: {
    type: Object,
    default: () => ({}),
  },
});

const emit = defineEmits(['update:modelValue']);

const teams = useMapGetter('teams/getTeams');

const opConfig = computed({
  get: () => props.modelValue || {},
  set: val => emit('update:modelValue', val),
});

const allDays = [
  { id: 'seg', label: 'Segunda' },
  { id: 'ter', label: 'Terça' },
  { id: 'qua', label: 'Quarta' },
  { id: 'qui', label: 'Quinta' },
  { id: 'sex', label: 'Sexta' },
  { id: 'sab', label: 'Sábado' },
  { id: 'dom', label: 'Domingo' },
];

const toggleDay = dayId => {
  const currentDays = [...(opConfig.value.dias_funcionamento || [])];
  const idx = currentDays.indexOf(dayId);
  if (idx >= 0) {
    currentDays.splice(idx, 1);
  } else {
    currentDays.push(dayId);
  }
  opConfig.value.dias_funcionamento = currentDays;
};

const addHoliday = () => {
  if (!opConfig.value.holidays) opConfig.value.holidays = [];
  opConfig.value.holidays.push({ data: '', descricao: '' });
};

const removeHoliday = idx => {
  const h = opConfig.value.holidays[idx];
  if (h.id) {
    h._destroy = true;
  } else {
    opConfig.value.holidays.splice(idx, 1);
  }
};

const addTeamMapping = () => {
  if (!opConfig.value.team_mappings) opConfig.value.team_mappings = [];
  opConfig.value.team_mappings.push({
    valor_campo: '',
    chatwoot_team_id: null,
    label_segmento: '',
  });
};

const removeTeamMapping = idx => {
  const tm = opConfig.value.team_mappings[idx];
  if (tm.id) {
    tm._destroy = true;
  } else {
    opConfig.value.team_mappings.splice(idx, 1);
  }
};
</script>

<template>
  <div class="space-y-6 max-w-4xl">
    <!-- Horários e Dias -->
    <div class="bg-n-surface-1 border border-n-weak rounded-xl p-6 space-y-4">
      <h3 class="text-base font-semibold text-n-slate-12">
        Horário de Atendimento Humano
      </h3>
      <p class="text-xs text-n-slate-11">
        A Isa Leal utiliza esses parâmetros para informar se a equipe humana
        está em horário comercial.
      </p>

      <div class="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2">
        <div>
          <label class="block text-xs font-semibold text-n-slate-12 mb-1.5">
            Horário de Abertura
          </label>
          <input
            v-model="opConfig.hora_abertura"
            type="time"
            class="reset-base !mb-0 w-full text-sm bg-n-surface-2 border border-n-weak rounded-lg px-3 py-2 text-n-slate-12 focus:border-n-brand outline-none"
          />
        </div>

        <div>
          <label class="block text-xs font-semibold text-n-slate-12 mb-1.5">
            Horário de Fechamento
          </label>
          <input
            v-model="opConfig.hora_fechamento"
            type="time"
            class="reset-base !mb-0 w-full text-sm bg-n-surface-2 border border-n-weak rounded-lg px-3 py-2 text-n-slate-12 focus:border-n-brand outline-none"
          />
        </div>
      </div>

      <div class="pt-2">
        <label class="block text-xs font-semibold text-n-slate-12 mb-2">
          Dias de Funcionamento
        </label>
        <div class="flex flex-wrap gap-2">
          <button
            v-for="day in allDays"
            :key="day.id"
            type="button"
            class="px-3 py-1.5 rounded-lg text-xs font-semibold border transition-all cursor-pointer"
            :class="
              (opConfig.dias_funcionamento || []).includes(day.id)
                ? 'bg-n-brand text-n-slate-1 border-n-brand shadow-sm'
                : 'bg-n-surface-2 text-n-slate-11 border-n-weak hover:border-n-brand/40'
            "
            @click="toggleDay(day.id)"
          >
            {{ day.label }}
          </button>
        </div>
      </div>
    </div>

    <!-- Feriados -->
    <div class="bg-n-surface-1 border border-n-weak rounded-xl p-6 space-y-4">
      <div class="flex items-center justify-between">
        <div>
          <h3 class="text-base font-semibold text-n-slate-12">
            Feriados & Recessos
          </h3>
          <p class="text-xs text-n-slate-11">
            Datas em que o atendimento humano não estará disponível.
          </p>
        </div>
        <Button
          sm
          ghost
          icon="i-lucide-plus"
          label="Adicionar Feriado"
          @click="addHoliday"
        />
      </div>

      <div class="space-y-2">
        <div
          v-for="(h, idx) in (opConfig.holidays || []).filter(
            item => !item._destroy
          )"
          :key="idx"
          class="flex items-center gap-3 bg-n-surface-2 p-3 rounded-lg border border-n-weak"
        >
          <input
            v-model="h.data"
            type="date"
            class="reset-base !mb-0 w-44 text-xs font-mono bg-n-surface-1 border border-n-weak rounded-lg px-2.5 py-1.5 text-n-slate-12 focus:border-n-brand outline-none"
          />
          <input
            v-model="h.descricao"
            type="text"
            placeholder="Descrição (ex: Carnaval / Natal)"
            class="reset-base !mb-0 flex-1 text-xs bg-n-surface-1 border border-n-weak rounded-lg px-2.5 py-1.5 text-n-slate-12 focus:border-n-brand outline-none"
          />
          <Button
            ghost
            ruby
            sm
            icon="i-lucide-trash-2"
            @click="removeHoliday(idx)"
          />
        </div>
      </div>
    </div>

    <!-- Parâmetros de Notificação e IA -->
    <div class="bg-n-surface-1 border border-n-weak rounded-xl p-6 space-y-4">
      <h3 class="text-base font-semibold text-n-slate-12">
        WhatsApp & Modelo de IA
      </h3>

      <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label class="block text-xs font-semibold text-n-slate-12 mb-1.5">
            Número WhatsApp Admin (Notificações)
          </label>
          <input
            v-model="opConfig.numero_admin"
            type="text"
            placeholder="Ex: 5511999999999"
            class="reset-base !mb-0 w-full text-sm font-mono bg-n-surface-2 border border-n-weak rounded-lg px-3 py-2 text-n-slate-12 focus:border-n-brand outline-none"
          />
        </div>

        <div>
          <label class="block text-xs font-semibold text-n-slate-12 mb-1.5">
            ID Grupo WhatsApp de Notificação
          </label>
          <input
            v-model="opConfig.grupo_whatsapp"
            type="text"
            placeholder="Ex: 12036304...-group@g.us"
            class="reset-base !mb-0 w-full text-sm font-mono bg-n-surface-2 border border-n-weak rounded-lg px-3 py-2 text-n-slate-12 focus:border-n-brand outline-none"
          />
        </div>

        <div>
          <label class="block text-xs font-semibold text-n-slate-12 mb-1.5">
            Modelo de IA
          </label>
          <input
            v-model="opConfig.modelo_ia"
            type="text"
            placeholder="Ex: gpt-4o, gpt-4o-mini, claude-3-5-sonnet-20241022"
            class="reset-base !mb-0 w-full text-sm font-mono bg-n-surface-2 border border-n-weak rounded-lg px-3 py-2 text-n-slate-12 focus:border-n-brand outline-none"
          />
        </div>

        <div>
          <label class="block text-xs font-semibold text-n-slate-12 mb-1.5">
            Label Chatwoot: Lead Qualificado
          </label>
          <input
            v-model="opConfig.label_qualificado"
            type="text"
            placeholder="Ex: lead_qualificado"
            class="reset-base !mb-0 w-full text-sm font-mono bg-n-surface-2 border border-n-weak rounded-lg px-3 py-2 text-n-slate-12 focus:border-n-brand outline-none"
          />
        </div>

        <div>
          <label class="block text-xs font-semibold text-n-slate-12 mb-1.5">
            Label Chatwoot: Lead Desqualificado
          </label>
          <input
            v-model="opConfig.label_desqualificado"
            type="text"
            placeholder="Ex: lead_desqualificado"
            class="reset-base !mb-0 w-full text-sm font-mono bg-n-surface-2 border border-n-weak rounded-lg px-3 py-2 text-n-slate-12 focus:border-n-brand outline-none"
          />
        </div>
      </div>
    </div>

    <!-- Mapeamento de Times por Segmento -->
    <div class="bg-n-surface-1 border border-n-weak rounded-xl p-6 space-y-4">
      <div class="flex items-center justify-between">
        <div>
          <h3 class="text-base font-semibold text-n-slate-12">
            Roteamento de Times por Segmento
          </h3>
          <p class="text-xs text-n-slate-11">
            Associe o valor coletado de um segmento ao Time do Chatwoot
            correspondente.
          </p>
        </div>
        <Button
          sm
          ghost
          icon="i-lucide-plus"
          label="Adicionar Mapeamento"
          @click="addTeamMapping"
        />
      </div>

      <div class="space-y-2">
        <div
          v-for="(tm, idx) in (opConfig.team_mappings || []).filter(
            item => !item._destroy
          )"
          :key="idx"
          class="flex items-center gap-3 bg-n-surface-2 p-3 rounded-lg border border-n-weak"
        >
          <input
            v-model="tm.valor_campo"
            type="text"
            placeholder="Valor do segmento (ex: Infantil)"
            class="reset-base !mb-0 w-44 text-xs font-mono bg-n-surface-1 border border-n-weak rounded-lg px-2.5 py-1.5 text-n-slate-12 focus:border-n-brand outline-none"
          />

          <select
            v-model="tm.chatwoot_team_id"
            class="reset-base !mb-0 flex-1 text-xs bg-n-surface-1 border border-n-weak rounded-lg px-2.5 py-1.5 text-n-slate-12 focus:border-n-brand outline-none"
          >
            <option :value="null">Selecione o Time do Chatwoot</option>
            <option v-for="team in teams" :key="team.id" :value="team.id">
              {{ team.name }} (ID: {{ team.id }})
            </option>
          </select>

          <input
            v-model="tm.label_segmento"
            type="text"
            placeholder="Label Chatwoot (ex: seg_infantil)"
            class="reset-base !mb-0 w-40 text-xs font-mono bg-n-surface-1 border border-n-weak rounded-lg px-2.5 py-1.5 text-n-slate-12 focus:border-n-brand outline-none"
          />

          <Button
            ghost
            ruby
            sm
            icon="i-lucide-trash-2"
            @click="removeTeamMapping(idx)"
          />
        </div>
      </div>
    </div>
  </div>
</template>
